import { getSnapshot } from 'mobx-state-tree';
import React, { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';
import { useHistory } from 'react-router-dom';
import Box from 'ui-box';
import {
  Button,
  Card,
  Container,
  FormField,
  Grid,
  Heading,
  majorScale,
  NumberInput,
  PageContainer,
  Spinner,
  useToast,
} from '../../../npaper';
import { IOrganisation, useStores } from '../../../stores';

interface FormValues {
  next_offer_number: number;
  next_order_number: number;
  next_invoice_number: number;
}

export const InvoicingPage = () => {
  const history = useHistory();
  const toaster = useToast();

  const { organisationStore, authStore } = useStores();
  const [organisation, setOrganisation] = useState<IOrganisation>();

  const { control, reset, errors, handleSubmit, formState } =
    useForm<FormValues>();

  useEffect(() => {
    if (authStore.currentOrganisation) {
      const org = organisationStore.itemMap.get(
        authStore.currentOrganisation.id + ''
      );
      setOrganisation(org!);
      reset({
        next_offer_number: org!.next_offer_number,
        next_order_number: org!.next_order_number,
        next_invoice_number: org!.next_invoice_number,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authStore]);

  const onSubmit = async (values: FormValues) => {
    if (!organisation) return;
    try {
      await organisationStore.updateItem(organisation, {
        ...getSnapshot(organisation),
        ...values,
      });

      history.length > 0 ? history.goBack() : history.push('/settings');
    } catch (err) {
      toaster.showDefaultError(err);
    }
  };

  return (
    <PageContainer>
      <Container>
        <Button
          iconLeft="arrow-left"
          marginBottom={majorScale(2)}
          onClick={() =>
            history.length > 0 ? history.goBack() : history.push('/settings')
          }
        >
          Indstillinger
        </Button>

        {!organisation ? (
          <Spinner />
        ) : (
          <Grid>
            <Card className="col-span-12 md:col-span-12 lg:col-span-10 xl:col-span-8">
              <div className="dialog">
                <Box className="header">
                  <Heading size={400}>Faktureringsindstillinger</Heading>
                </Box>
                <Grid padding={majorScale(2)}>
                  <Heading size={300} className="col-span-12">
                    Nummerering
                  </Heading>
                  <FormField
                    className="col-span-9"
                    label="Næste tilbudsnummer"
                    inline
                  >
                    <Controller
                      control={control}
                      name="next_offer_number"
                      render={({ value, onChange, onBlur }) => (
                        <NumberInput
                          placeholder="Tilbudsnummer"
                          maximumFractionDigits={0}
                          minimumFractionDigits={0}
                          onBlur={onBlur}
                          value={value}
                          validationMessage={
                            errors.next_invoice_number &&
                            errors.next_invoice_number.message
                          }
                          isInvalid={
                            errors.next_invoice_number &&
                            !!errors.next_invoice_number.message
                          }
                          onChange={(n) => onChange(n)}
                        />
                      )}
                    />
                  </FormField>

                  <FormField
                    className="col-span-9"
                    label="Næste ordrenummer"
                    inline
                  >
                    <Controller
                      control={control}
                      name="next_order_number"
                      render={({ value, onChange, onBlur }) => (
                        <NumberInput
                          placeholder="Ordrenummer"
                          maximumFractionDigits={0}
                          minimumFractionDigits={0}
                          onBlur={onBlur}
                          value={value}
                          validationMessage={
                            errors.next_invoice_number &&
                            errors.next_invoice_number.message
                          }
                          isInvalid={
                            errors.next_invoice_number &&
                            !!errors.next_invoice_number.message
                          }
                          onChange={(n) => onChange(n)}
                        />
                      )}
                    />
                  </FormField>

                  <FormField
                    className="col-span-9"
                    label="Næste fakturanummer"
                    inline
                  >
                    <Controller
                      control={control}
                      name="next_invoice_number"
                      render={({ value, onChange, onBlur }) => (
                        <NumberInput
                          placeholder="Fakturanummer"
                          maximumFractionDigits={0}
                          minimumFractionDigits={0}
                          onBlur={onBlur}
                          value={value}
                          validationMessage={
                            errors.next_invoice_number &&
                            errors.next_invoice_number.message
                          }
                          isInvalid={
                            errors.next_invoice_number &&
                            !!errors.next_invoice_number.message
                          }
                          onChange={(n) => onChange(n)}
                        />
                      )}
                    />
                  </FormField>
                </Grid>

                <Box className="actions">
                  <Button
                    disabled={formState.isSubmitting}
                    onClick={() => history.push('/settings')}
                  >
                    <FormattedMessage
                      id="defaults.cancel"
                      defaultMessage="Cancel"
                    />
                  </Button>
                  <Button
                    primary
                    isLoading={formState.isSubmitting}
                    onClick={handleSubmit(onSubmit)}
                  >
                    <FormattedMessage
                      id="defaults.save-changes"
                      defaultMessage="Save changes"
                    />
                  </Button>
                </Box>
              </div>
            </Card>
          </Grid>
        )}
      </Container>
    </PageContainer>
  );
};
